import classNames from 'classnames'
import { Icon } from 'components/common/Icon'
import useTranslation from 'next-translate/useTranslation'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import bannerCollectibleMobile from '../../../public/files/release-calendar/banner-collectible-mobile-resized.png'
import bannerCollectible from '../../../public/files/release-calendar/banner-collectible-resized-3.png'
import bannerPrint from '../../../public/files/release-calendar/banner-print-2-resized-2.png'
import bannerPrintMobile from '../../../public/files/release-calendar/banner-print-mobile-resized.png'
import bannerSculpture from '../../../public/files/release-calendar/banner-sculpture-2-resized-2.png'
import bannerSculptureMobile from '../../../public/files/release-calendar/banner-sculpture-mobile-resized.png'
import classes from './Banner.module.scss'

const benefits = [
  'view_upcoming_releases',
  'set_email_reminders',
  'updated_daily',
]

const mediums = ['print', 'sculpture', 'collectible']

const MediumToImageMap = {
  print: bannerPrint,
  sculpture: bannerSculpture,
  collectible: bannerCollectible,
} as const

const MediumToImageMapMobile = {
  print: bannerPrintMobile,
  sculpture: bannerSculptureMobile,
  collectible: bannerCollectibleMobile,
} as const

export const ReleaseCalendarBanner = () => {
  const { t } = useTranslation('release_calendar')
  const [medium, setMedium] = useState(mediums[0])
  const [mediumComplete, setMediumComplete] = useState(false)
  const [mediumLength, setMediumLength] = useState(0)
  const [imageTransition, setImageTransition] = useState({
    fadeIn: false,
    fadeOut: false,
  })

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>

    const displayedMedium = t(`common:${medium}`)
    const nextLength = mediumLength + 1
    const prevLength = mediumLength - 1

    if (mediumComplete) {
      if (prevLength < 0) {
        const currentIndex = mediums.findIndex(m => m === medium)
        const nextIndex = currentIndex + 1

        if (currentIndex < 0) {
          setMedium(mediums[0])
        } else {
          if (nextIndex < mediums.length) {
            setMedium(mediums[nextIndex])
          } else {
            setMedium(mediums[0])
          }
        }
        setMediumComplete(false)
        setImageTransition({ fadeIn: true, fadeOut: false })
        return
      }

      timeout = setTimeout(() => {
        setMediumLength(prevLength)
      }, 250)

      return () => clearTimeout(timeout)
    }

    if (mediumLength < displayedMedium.length) {
      timeout = setTimeout(() => {
        setMediumLength(nextLength)
      }, 125)

      return () => clearTimeout(timeout)
    }

    timeout = setTimeout(() => {
      setMediumComplete(true)
      setImageTransition({ fadeIn: false, fadeOut: true })
    }, 2500)

    return () => {
      clearTimeout(timeout)
    }
  }, [medium, mediumLength, mediumComplete, t])

  const getImageProps = (med: string, isMobile: boolean) => {
    const isPrint = med === mediums[0]
    const priority = isPrint && !isMobile

    return {
      className: classNames(
        'object-contain sm:object-cover',
        medium !== med && 'hidden',
        !isPrint && 'p-5'
      ),
      quality: 100,
      sizes: isMobile
        ? '(max-width:639px) 100vw, 0vw'
        : '(max-width:639px) 0vw, 100vw',
      priority,
      loading: priority ? 'eager' : 'lazy',
      fill: true,
    } as const
  }

  const titleTexts = {
    never_miss_a: t('never_miss_a'),
    medium: mediumLength
      ? t(`common:${medium}`).slice(0, mediumLength)
      : 'empty',
    release_again: t('release_again'),
  }

  return (
    <section className='relative w-full overflow-hidden bg-warm-50 px-6 pb-6 pt-8 sm:h-[18.75rem] sm:pb-8 md:px-12 xl:py-5'>
      <div className='mx-auto flex h-full max-w-[90rem] items-center justify-between sm:space-x-10 2xl:space-x-20'>
        <div>
          <h1
            className={classNames(
              'relative z-[1] w-full whitespace-pre-line font-body text-3xl font-normal tracking-normal sm:text-2xl lg:text-3xl'
            )}
          >
            <div className='sm:inline'>{titleTexts.never_miss_a}</div>

            <em
              className={classNames(
                'block w-max overflow-hidden whitespace-nowrap border-r-[0.1em] not-italic text-purple-500 sm:mx-1 sm:inline-flex',
                classes['typewriter'],
                !mediumLength ? 'invisible' : ''
              )}
            >
              {titleTexts.medium}
            </em>

            <div>{titleTexts.release_again}</div>
          </h1>

          <h2 className='mt-2 hidden font-body text-sm font-normal tracking-normal text-cool-600 md:block lg:text-base xl:text-xl'>
            {t('the_lastest_releases')}
          </h2>

          <ul className='mt-4 space-y-2 sm:mt-6 lg:flex lg:items-center lg:space-x-3 lg:space-y-0'>
            {benefits.map(b => (
              <li key={b} className='flex items-center space-x-1.5'>
                <div>
                  <Icon
                    name='filled-success'
                    filled
                    className='text-success-default'
                  />
                </div>

                <h3 className='font-body text-sm font-normal tracking-normal text-cool-600 xl:text-base'>
                  {t(b)}
                </h3>
              </li>
            ))}
          </ul>
        </div>

        <div
          className={classNames(
            'absolute top-4 h-full sm:static',
            imageTransition.fadeIn && classes['fade-in'],
            imageTransition.fadeOut && classes['fade-out'],
            {
              '-right-14': medium === mediums[0],
              '-right-16': medium === mediums[1],
              '-right-[4.5rem]': medium === mediums[2],
            }
          )}
        >
          <div className='relative hidden h-full w-[250px] items-center justify-center sm:flex lg:w-[350px]'>
            <Image
              src={MediumToImageMap.print}
              alt='Print'
              {...getImageProps(mediums[0], false)}
            />

            {/* these two images should have a smaller size than the first one */}
            {/* we want the first one to be recognized as lcp */}
            <div className='pointer-events-none relative h-18/20 w-18/20'>
              <Image
                src={MediumToImageMap.sculpture}
                alt='Sculpture'
                {...getImageProps(mediums[1], false)}
              />

              <Image
                src={MediumToImageMap.collectible}
                alt='Collectible'
                {...getImageProps(mediums[2], false)}
              />
            </div>
          </div>

          <div className='relative h-full w-[161px] sm:hidden'>
            <Image
              src={MediumToImageMapMobile.print}
              alt='Print'
              {...getImageProps(mediums[0], true)}
            />

            <Image
              src={MediumToImageMapMobile.sculpture}
              alt='Sculpture'
              {...getImageProps(mediums[1], true)}
            />

            <Image
              src={MediumToImageMapMobile.collectible}
              alt='Collectible'
              {...getImageProps(mediums[2], true)}
            />
          </div>
        </div>
      </div>
    </section>
  )
}
